<!--
  Campaign-wide dial-attempt log -- every dial this campaign made, connected
  or not (listTaskDialAttempts), closing the gap the "Call history" tab next
  to this one has always had: that tab only ever shows calls that connected
  long enough to leave a recording, so an unanswered dial is invisible there,
  which reads to an admin as "the dialer isn't doing anything." This tab
  answers that directly -- the summary strip at the top leads with
  connected-vs-total so that ambiguity never has to be guessed at again.

  Sibling to CampaignCallHistoryPanel.vue: same toolbar/list/pagination/
  CallIntelligenceDrawer shape, reusing that exact drawer (unmodified) for
  the COMPLETED-row drill-in. NOT_ANSWERED rows never had a recording to
  begin with, so they don't open the drawer at all -- see DialAttemptRow.vue.
-->
<template>
  <div class="cda-root tda-scope">
    <CallIntelligenceDrawer
      v-model="showIntelligenceDrawer"
      :call="selectedCall"
      :organization="organization"
    />

    <div class="cda-toolbar">
      <span class="cda-toolbar__count">
        {{ totalAttempts }} attempt{{ totalAttempts === 1 ? '' : 's' }}
      </span>
      <v-btn
        variant="outlined"
        size="small"
        class="text-none"
        prepend-icon="mdi-refresh"
        :loading="loading"
        :disabled="!isSessionReady"
        @click="handleRefresh"
      >
        Refresh
      </v-btn>
    </div>

    <div v-if="totalAttempts" class="da-summary">
      <span class="da-summary__stat">
        <strong>{{ totalAttempts }}</strong> attempt{{ totalAttempts === 1 ? '' : 's' }}
      </span>
      <span class="da-summary__divider" aria-hidden="true" />
      <span class="da-summary__stat da-summary__stat--success">
        <v-icon icon="mdi-phone-check-outline" size="14" />
        <strong>{{ connectedCount }}</strong> connected
      </span>
      <span class="da-summary__stat da-summary__stat--danger">
        <v-icon icon="mdi-phone-missed" size="14" />
        <strong>{{ notAnsweredCount }}</strong> not answered
      </span>
      <span class="da-summary__spacer" />
      <span class="da-summary__stat">{{ connectRateLabel }} connect rate</span>
    </div>

    <DialAttemptsList
      :attempts="paginatedAttempts"
      :loading="loading"
      @open-detail="openCallDetail"
    />

    <PortalListPagination
      class="cda-footer"
      :page="page"
      :page-count="pageCount"
      :items-per-page="itemsPerPage"
      :range-label="rangeLabel"
      :total="totalAttempts"
      @update:page="setPage"
      @update:items-per-page="setItemsPerPage"
    />
  </div>
</template>

<script setup>
import { ref, computed, toRef } from 'vue'
import { useStore } from 'vuex'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import CallIntelligenceDrawer from '@/components/CallHistory/CallIntelligenceDrawer.vue'
import DialAttemptsList from '@/components/CallHistory/DialAttemptsList.vue'
import {
  useTaskDialAttemptsData,
  getDialAttemptsErrorMessage,
} from '@/composables/callHistory/useTaskDialAttemptsData'
import '@/styles/call-history/dial-attempts.css'

defineOptions({ name: 'CampaignDialAttemptsPanel' })

const props = defineProps({
  campaignId: { type: String, default: '' },
})

const emit = defineEmits(['error'])

const store = useStore()
const organization = computed(() => store.state.organization)
const showIntelligenceDrawer = ref(false)
const selectedCall = ref(null)

const {
  loading,
  page,
  itemsPerPage,
  pageCount,
  isSessionReady,
  paginatedAttempts,
  totalAttempts,
  connectedCount,
  notAnsweredCount,
  rangeLabel,
  loadDialAttempts,
  setPage,
  setItemsPerPage,
} = useTaskDialAttemptsData({ taskId: toRef(props, 'campaignId') })

const connectRateLabel = computed(() => {
  if (!totalAttempts.value) return '0%'
  return `${Math.round((connectedCount.value / totalAttempts.value) * 100)}%`
})

async function handleRefresh() {
  try {
    const result = await loadDialAttempts()
    if (result && result.status === 'ERROR') {
      emit('error', getDialAttemptsErrorMessage(result))
    }
  } catch (err) {
    emit('error', getDialAttemptsErrorMessage(err))
  }
}

function openCallDetail(call) {
  if (!call || call.outcome !== 'COMPLETED') return
  selectedCall.value = { ...call }
  showIntelligenceDrawer.value = true
}
</script>

<style scoped>
.cda-root {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cda-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.cda-toolbar__count {
  font-weight: 650;
  font-size: 0.9rem;
  opacity: 0.75;
}
</style>
